import { useState } from "react";
import { useBudget } from "../context/BudgetContext";

const COLORS = ["var(--green)", "var(--blue)", "var(--amber)", "var(--purple)", "var(--red)"];
const HOVERS = ["hover-green", "hover-blue", "hover-amber", "hover-purple", "hover-red"];

export default function SpendingGroups() {
  const { categories, getCategorySpending, budgetLoading, groups, updateGroups } = useBudget();
  const [newName, setNewName] = useState("");
  const [colorIdx, setColorIdx] = useState(0);
  const [confirmId, setConfirmId] = useState(null);

  if (budgetLoading) return <div className="page-loading" />;

  const addGroup = () => {
    if (!newName.trim()) return;
    updateGroups([...groups, { id: Date.now(), name: newName.trim(), colorIdx, categoryIds: [] }]);
    setNewName("");
    setColorIdx((colorIdx + 1) % COLORS.length);
  };

  const deleteGroup = (id) => {
    updateGroups(groups.filter(g => g.id !== id));
    setConfirmId(null);
  };

  const toggleCat = (groupId, catId) => {
    updateGroups(groups.map(g => {
      if (g.id !== groupId) return g;
      return {
        ...g,
        categoryIds: g.categoryIds.includes(catId)
          ? g.categoryIds.filter(id => id !== catId)
          : [...g.categoryIds, catId],
      };
    }));
  };

  const getGroupSpend = (group) => group.categoryIds.reduce((sum, id) => sum + getCategorySpending(id), 0);
  const unassigned = categories.filter(cat => !groups.some(g => g.categoryIds.includes(cat.id)));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Spending Groups</div>
          <div className="page-subtitle">$ group categories for analysis</div>
        </div>
      </div>

      {/* Add group */}
      <div className="card hover-green" style={{ marginBottom: 8 }}>
        <div className="label" style={{ marginBottom: 8 }}>NEW_GROUP</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input placeholder="e.g. fixed expenses" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && addGroup()} />
          <div style={{ display: "flex", gap: 4 }}>
            {COLORS.map((c, i) => (
              <button key={i} onClick={() => setColorIdx(i)} style={{ width: 16, height: 16, borderRadius: "50%", background: c, border: colorIdx === i ? "2px solid #fff" : "2px solid transparent", cursor: "pointer", padding: 0 }} />
            ))}
          </div>
          <button className="btn btn-green" onClick={addGroup}>+ add</button>
        </div>
      </div>

      {/* Groups */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {groups.map(group => {
          const spend = getGroupSpend(group);
          const assigned = categories.filter(c => group.categoryIds.includes(c.id));
          const color = COLORS[group.colorIdx];

          return (
            <div key={group.id} className={`card ${HOVERS[group.colorIdx]}`} style={{ borderLeft: `2px solid ${color}44` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="mono" style={{ fontSize: 12, color: "#888" }}>{group.name.toLowerCase()}</span>
                  <span className="mono" style={{ fontSize: 12, color }}>${spend.toLocaleString()}</span>
                </div>
                {confirmId === group.id ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="btn btn-danger" onClick={() => deleteGroup(group.id)} style={{ padding: "2px 7px", fontSize: 9 }}>sure?</button>
                    <button className="btn" onClick={() => setConfirmId(null)} style={{ padding: "2px 5px", fontSize: 9 }}>✕</button>
                  </div>
                ) : (
                  <button className="btn btn-danger" onClick={() => setConfirmId(group.id)} style={{ padding: "2px 7px", fontSize: 9 }}>del</button>
                )}
              </div>

              {assigned.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
                  {assigned.map(cat => (
                    <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: 5, background: "#050505", border: "1px solid #111", borderRadius: 4, padding: "3px 7px" }}>
                      <span className="mono" style={{ fontSize: 10, color: "#555" }}>{cat.name}</span>
                      <span className="mono" style={{ fontSize: 9, color: "#666" }}>${getCategorySpending(cat.id).toLocaleString()}</span>
                      <button onClick={() => toggleCat(group.id, cat.id)} style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 9, padding: 0, lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {unassigned.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {unassigned.map(cat => (
                    <button key={cat.id} onClick={() => toggleCat(group.id, cat.id)} className="btn" style={{ fontSize: 10, padding: "3px 8px", borderColor: `${color}22`, color }}>
                      + {cat.name}
                    </button>
                  ))}
                </div>
              )}

              {assigned.length === 0 && unassigned.length === 0 && (
                <span className="mono" style={{ fontSize: 10, color: "#555" }}>all categories assigned</span>
              )}
            </div>
          );
        })}
      </div>

      {groups.length === 0 && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <span className="mono" style={{ fontSize: 11, color: "#555" }}>add a group above to start organising categories</span>
        </div>
      )}

      {categories.length === 0 && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <span className="mono" style={{ fontSize: 11, color: "#555" }}>add categories first to start grouping</span>
        </div>
      )}
    </div>
  );
}
